import * as React from 'react';
const listeners = {};
// This component is needed to bind events to the most outer
// component but still use Reacts event system. If we were to
// bind directly to `document` you won't be able to stop the
// propagation to those handlers from inside React, since React
// only binds one handler to `document`.
export default (props) => {
    const rootProps = Object.assign(Object.assign({}, props), { onClick: e => {
            if (props.onClick) {
                props.onClick(e);
            }
            triggerEvent(e);
        }, onKeyDown: e => {
            if (props.onKeyDown) {
                props.onKeyDown(e);
            }
            triggerEvent(e);
        } });
    return React.createElement("div", Object.assign({}, rootProps));
};
export function on(event, callback) {
    if (!__BROWSER__) {
        console.warn('Root.on should not be called on the server');
        return;
    }
    const events = event.split(' ');
    events.forEach(eventName => {
        if (!listeners[eventName]) {
            listeners[eventName] = [];
        }
        listeners[eventName].push(callback);
        handleWindowSubscription(eventName);
    });
    // We return a function here that you can call to unsubscribe to the event.
    // This instead of exporting an `off` function where you have to pass
    // the same function you passed to `on` which makes it impossible to
    // pass an inline function to `on`.
    return () => events.forEach(eventName => {
        listeners[eventName] = listeners[eventName].filter(listener => listener !== callback);
        handleWindowSubscription(eventName);
    });
}
export function scrollPosition(newPosition) {
    const element = document.scrollingElement || document.documentElement || document.body;
    if (newPosition !== undefined) {
        element.scrollTop = newPosition;
        triggerEvent({ type: 'scope:setscroll', position: newPosition });
    }
    return element.scrollTop;
}
export function scrollTo(to, duration) {
    window.scrollTo({ top: to, behavior: 'smooth' });
}
export function one(event, callback) {
    if (!__BROWSER__) {
        console.warn('one should not be called during server-side rendering');
        return;
    }
    const unsubscribe = on(event, (e) => {
        callback(e);
        unsubscribe();
    });
}
export function offsetTop(element) {
    if (!element) {
        return 0;
    }
    return element.offsetTop + offsetTop(element.offsetParent);
}
function hasSubscribers(eventName) {
    return listeners[eventName] && listeners[eventName].length > 0;
}
export function triggerEvent(e) {
    if (!hasSubscribers(e.type)) {
        return;
    }
    listeners[e.type].forEach(listener => {
        listener(e);
    });
}
const throttling = [];
// Some events than can fire at a high rate and execute computationally
// expensive operations such as DOM modifications. Those events should
// be throttled, such as `scroll` and `resize`.
function throttleEvent(e) {
    if (!hasSubscribers(e.type) || throttling.indexOf(e.type) !== -1) {
        return;
    }
    throttling.push(e.type);
    window.requestAnimationFrame(() => {
        triggerEvent(e);
        throttling.splice(throttling.indexOf(e.type), 1);
    });
}
const windowEvents = {
    resize: throttleEvent,
    scroll: throttleEvent,
    // We need `touchmove` on the window because you can't swipe out
    // the drawer/mini cart
    touchmove: throttleEvent,
    touchstart: throttleEvent,
    touchend: throttleEvent,
};
const boundWindowEvents = [];
// Pass `window` events defined in `windowEvents` to this module. Should be
// invoked when `listeners` has been manipulated
function handleWindowSubscription(eventName) {
    if (windowEvents[eventName]) {
        if (hasSubscribers(eventName) && !boundWindowEvents.includes(eventName)) {
            window.addEventListener(eventName, windowEvents[eventName]);
            boundWindowEvents.push(eventName);
        }
        else if (!hasSubscribers(eventName) && boundWindowEvents.includes(eventName)) {
            window.removeEventListener(eventName, windowEvents[eventName]);
            boundWindowEvents.splice(boundWindowEvents.indexOf(eventName), 1);
        }
    }
}
